import { useState, useCallback, useMemo, useEffect } from "react";
import { useBlastCampaign } from "../../context";
import { 
  parseCSVFile, 
  validateCSVFile, 
  validateRequiredMappings,
  autoMapFields,
  getSampleValue,
  getPreviewRows,
  getMappedFields,
  PREVIEW_ROW_COUNT,
} from "../../utils/csvHelpers";
import { findDuplicateEmails } from "../../utils/fieldValidation";
import {
  MAX_FILE_SIZE_BYTES,
  ALLOWED_FILE_TYPES,
  SYSTEM_FIELD_MAPPINGS,
} from "../../constants/blastConstants";

/**
 * Hook for managing CSV file upload and field mapping
 */
export const useContactsMapping = () => {
  const {
    fileUploaded,
    fileUploadedHeaders,
    csvParsedContacts,
    fieldMappings,
    isCsvMapped,
    setCsvFile,
    setFieldMapping,
    setFieldMappings,
    applyCsvMappings,
    clearCsvData,
  } = useBlastCampaign();

  const [isParsingFile, setIsParsingFile] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [autoMappedCount, setAutoMappedCount] = useState(0);

  /**
   * Auto-map fields when headers change
   */
  useEffect(() => {
    if (fileUploadedHeaders.length > 0 && !isCsvMapped) {
      const autoMappings = autoMapFields(fileUploadedHeaders);
      const mappedCount = Object.keys(autoMappings).length;
      
      if (mappedCount > 0) {
        setFieldMappings(autoMappings);
        setAutoMappedCount(mappedCount);
      }
    }
  }, [fileUploadedHeaders, isCsvMapped, setFieldMappings]);

  /**
   * Handle file upload
   */
  const handleFileUpload = useCallback(
    async (file) => {
      setParseError(null);
      setAutoMappedCount(0);

      // Validate file
      const validation = validateCSVFile(file, MAX_FILE_SIZE_BYTES, ALLOWED_FILE_TYPES);
      if (!validation.isValid) {
        setParseError(validation.error);
        return { success: false, error: validation.error };
      }

      setIsParsingFile(true);

      try {
        const result = await parseCSVFile(file);

        if (result.error) {
          setParseError(result.error);
          setIsParsingFile(false);
          return { success: false, error: result.error };
        }

        setCsvFile(file, result.headers, result.data);
        setIsParsingFile(false);
        return { success: true, headers: result.headers, rowCount: result.data.length };
      } catch (err) {
        const errorMessage = err.message || "Failed to parse CSV file";
        setParseError(errorMessage);
        setIsParsingFile(false);
        return { success: false, error: errorMessage };
      }
    },
    [setCsvFile]
  );

  /**
   * Handle field mapping change
   */
  const handleFieldMappingChange = useCallback(
    (systemField, csvColumn) => {
      setFieldMapping(systemField, csvColumn);
    },
    [setFieldMapping]
  );

  /**
   * Get available CSV columns for mapping (not already mapped)
   */
  const availableColumns = useMemo(() => {
    const mappedColumns = Object.values(fieldMappings).filter(Boolean);
    return fileUploadedHeaders.filter(
      (header) => !mappedColumns.includes(header.value)
    );
  }, [fileUploadedHeaders, fieldMappings]);

  /**
   * Get available system fields for mapping (not already mapped)
   */
  const availableSystemFields = useMemo(() => {
    const mappedFields = Object.entries(fieldMappings)
      .filter(([, value]) => value)
      .map(([key]) => key);
    return SYSTEM_FIELD_MAPPINGS.filter(
      (field) => !mappedFields.includes(field.mapping)
    );
  }, [fieldMappings]);

  /**
   * Check if required fields are mapped
   */
  const mappingValidation = useMemo(() => {
    const requiredFields = SYSTEM_FIELD_MAPPINGS
      .filter((f) => f.required)
      .map((f) => f.mapping);
    return validateRequiredMappings(fieldMappings, requiredFields);
  }, [fieldMappings]);

  /**
   * Check for duplicate emails in parsed contacts
   */
  const duplicateCheck = useMemo(() => {
    if (!csvParsedContacts.length || !fieldMappings.email) {
      return { hasDuplicates: false, duplicates: [] };
    }

    // Map contacts to use the email field mapping
    const contactsWithEmail = csvParsedContacts.map((contact) => ({
      email: contact[fieldMappings.email],
    }));

    return findDuplicateEmails(contactsWithEmail);
  }, [csvParsedContacts, fieldMappings.email]);

  /**
   * Save mappings and prepare contacts for blast
   */
  const saveMappings = useCallback(() => {
    if (!mappingValidation.isValid) {
      return {
        success: false,
        error: `Missing required fields: ${mappingValidation.missingFields.join(", ")}`,
      };
    }

    applyCsvMappings();
    return { success: true };
  }, [applyCsvMappings, mappingValidation]);

  /**
   * Clear file and reset mappings
   */
  const handleRemoveFile = useCallback(() => {
    setParseError(null);
    setAutoMappedCount(0);
    clearCsvData();
  }, [clearCsvData]);

  /**
   * Get sample value for a CSV column
   */
  const getColumnSampleValue = useCallback(
    (columnName) => getSampleValue(csvParsedContacts, columnName),
    [csvParsedContacts]
  );

  /**
   * Preview rows with mapped field names
   */
  const previewRows = useMemo(() => {
    return getPreviewRows(csvParsedContacts, fieldMappings, PREVIEW_ROW_COUNT);
  }, [csvParsedContacts, fieldMappings]);

  /**
   * Get list of currently mapped fields (for preview table columns)
   */
  const mappedFieldsList = useMemo(() => {
    return getMappedFields(fieldMappings);
  }, [fieldMappings]);

  return {
    // State
    fileUploaded,
    fileUploadedHeaders,
    csvParsedContacts,
    fieldMappings,
    isCsvMapped,
    isParsingFile,
    parseError,
    autoMappedCount,

    // Computed
    availableColumns,
    availableSystemFields,
    mappingValidation,
    duplicateCheck,
    contactCount: csvParsedContacts.length,
    previewRows,
    mappedFieldsList,

    // Actions
    handleFileUpload,
    handleFieldMappingChange,
    saveMappings,
    handleRemoveFile,
    getColumnSampleValue,
  };
};

export default useContactsMapping;
